import React, { Fragment, useEffect, useState } from 'react';
import { bool, func, string, object, number } from 'prop-types';
import WarningIcon from '@material-ui/icons/Warning';
import get from 'lodash/get';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  Grid,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowDown, Lock, ReportProblem } from '@material-ui/icons';
import { sentenceCase } from 'change-case';
import copy from 'copy-to-clipboard';
import moment from 'moment';

import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import { FormTextField, Loading } from '../../../../../components';
import { ALERT_ERROR, ALERT_SUCCESS, API_KEY_STATUS_DELETE_FAILED } from '../../../../../constants';

const displayItem = (applicationDetails, classes, item) => {
  const value = get(applicationDetails, item.key) || '';
  return (
    <div key={item.id} id={item.id} className={classes.leftPanelItemContainer}>
      <Typography className={classes.leftPanelTitle}>{item.label}</Typography>
         <Typography className={classes.leftPanelDesc}>
          {item.type === 'link'
          ? value && value.split(",").map(url=> <li key={url}><Link href={url}>{url}</Link></li>)
          :item.useSentenceCase ? sentenceCase(value) : value
          }
        </Typography>
    </div>
  );
};

const showCopied = (value, valueType, notifyMessages) => {
  let strType = '';
  if (valueType === 'API_KEY') {
    strType = 'API Key';
  } else if (valueType === 'SECRET') {
    strType = 'Secret';
  }
  notifyMessages(`${strType}: ${value} was copied to the clipboard.`, ALERT_SUCCESS, true);
};

const items = [
  {
    id: 'app-oauth-callback-url',
    key: 'oauthCallbackUrl',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.urls'),
    type: 'link',
  },
  {
    id: 'app-oauth-scope',
    key: 'oauthScope',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.scope'),
  },
  {
    id: 'app-oauth-type',
    key: 'oauthType',
    label: getI18nFormattedMessage('label.application.details.key.overview.oauth.type'),
    useSentenceCase: true,
  },
];

const SingleKeyView = ({
  keyDetails,
  classes,
  notifyMessages,
  setIsEditKeyOpen,
  isLocking,
  isApiKeyExpiryEnabled,
  locked,
  disabledByInternal,
  appSecretHashingMetadata,
  regeneratedSecretDetails,
  regerateSecretStatus,
  deleteKey,
  regenerateSecret,
  applicationUuid,
  defaultHashType,
  isEditDisabled,
  isPortalAdmin,
  isAdmin,
  onSaveDone,
  idx }) => {

    const intl = getI18n(useIntl());
    const [anchorEl, setAnchorEl] = useState(null);
    const [isSecretInfoDialogOpen, setIsSecretInfoDialogOpen] = useState(false);
    const [keyClientSecret, setKeyClientSecret] = useState('');
    const [showLoading, setShowLoading] = useState(false);
    const [generatingSecret, setGeneratingSecret] = useState(false);

    const handleGenerateSecretByType = (type) => {
      generateSecret(type);
      setAnchorEl(false);
    }

    const displayHashingOptions = get(appSecretHashingMetadata, 'plaintextAllowed');

    const handleGenerateSecretClick = (event) => {
      if(displayHashingOptions) {
        setAnchorEl(event.currentTarget);
      } else {
        generateSecret(defaultHashType);
      }
    };

    const handleMenuClose = () => {
      setAnchorEl(false);
    };

    useEffect(() => {
      if (generatingSecret) {
        if(regerateSecretStatus === 'SUCCESS') {
          const keySecret = get(regeneratedSecretDetails, 'keySecret');
          setKeyClientSecret(keySecret);
          setIsSecretInfoDialogOpen(true);
          setGeneratingSecret(false);
        } else if (regerateSecretStatus === 'FAIL') {
          notifyMessages(intl.getI18nMessage('label.application.details.keys.secret.update.error'), ALERT_ERROR);
          setGeneratingSecret(false);
        }
        setShowLoading(false);
      }
    },[regerateSecretStatus]);

    const generateSecret = (type) => {
      const { apiKey } = keyDetails;
      regenerateSecret({
        apiKey,
        appUuid: applicationUuid,
        keySecretHashed: type === 'HASHED_SECRET',
      });
      setShowLoading(true);
      setGeneratingSecret(true);
    };

    const getLeftPanelButton = () => {
      const { status, apiKey } = keyDetails;
      if (status === API_KEY_STATUS_DELETE_FAILED && isAdmin) {
        return (<Button
          onClick={() => {
            deleteKey({
              keyDetails: {
                appUuid: applicationUuid,
                apiKey,
              },
              isForceDelete: isPortalAdmin,
            });
          }}
          className={classes.forceDeleteButton}
        >
          {getI18nFormattedMessage(isPortalAdmin? 'label.application.details.keys.overview.force.delete.button': 'label.delete.button')}
        </Button>);
      }
      
      return (<Button
        variant="outlined"
        onClick={() => {
          setIsEditKeyOpen(true);
        }}
        className={classes.editKeyButton}
        disabled={locked || disabledByInternal
            || isEditDisabled || status === API_KEY_STATUS_DELETE_FAILED}
      >
        {getI18nFormattedMessage('label.application.details.keys.overview.editkey.button')}
      </Button>);
    };

    const renderSecretDialog = () => {
      const keySecretHashed = get(regeneratedSecretDetails, 'keySecretHashed');
      return (
        <Dialog
          open={isSecretInfoDialogOpen}
          onClose={() => {
            setIsSecretInfoDialogOpen(false)
          }}
          aria-labelledby="generate-secret-dialog"
          id="generate-secret-dialog"
          classes={{
            paper: classes.paperClass,
          }}
        >
          <div className={classes.generateSecretDialog}>
            <h3>{intl.getI18nFormattedMessage('label.application.details.keys.key.generate.secret.dialog.heading')}</h3>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-key-${keyClientSecret}`}
                name={intl.getI18nMessage('label.application.key.shared.secret')}
                value={keyClientSecret}
                fieldContainerClass={classes.dialogTextField}
                disabled
                errorHelperText={false}
                helperText={''}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyClientSecret);
                  showCopied(keyClientSecret, 'SECRET', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>
            {keySecretHashed &&
              <div className={classes.secretHelpText}>
                <WarningIcon className={classes.secretInfoWarningIcon}/>
                <Typography>
                  {intl.getI18nFormattedMessage('label.application.key.shared.secret.hashed.generated.help')}
                </Typography>
              </div>}
            <div className={classes.dialogButtonPanel}>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  onSaveDone();
                  setIsSecretInfoDialogOpen(false);
                }}
                className={classes.cancelButton}
              >
              {intl.getI18nFormattedMessage('label.close.button')}
              </Button>
            </div>
          </div>
        </Dialog>);
    };

  const getExpiryDateText = () => {
    let expiryDateText = 'None';
    const secretExpiryTs = keyDetails.secretExpiryTs;
    if (isApiKeyExpiryEnabled && secretExpiryTs) {
        const keyExpiryDate = moment(secretExpiryTs);
        expiryDateText = keyExpiryDate.format(
          'dddd, MMMM Do YYYY, HH:mm:ss',
        );
    }
    return expiryDateText;
  };

  const getExpiryDateSubText = () => {
    let expiryDateSubText = '';
    const secretExpiryTs = keyDetails.secretExpiryTs;
    const keyStatus = keyDetails.status;
    if (isApiKeyExpiryEnabled && secretExpiryTs) {
        const keyExpiryDate = moment(secretExpiryTs);
        const currentTime = moment();
        if (keyStatus === 'EXPIRED') {
            expiryDateSubText = getI18nFormattedMessage('label.application.details.apikeys.key.expired');
        } else {
            if (keyExpiryDate.diff(currentTime, 'days') >= 0) {
                expiryDateSubText = `${keyExpiryDate.diff(
                    currentTime,
                    'days',
                )} days`;
            } else {
                expiryDateSubText = '0 days';
            }
        }
    }
    return expiryDateSubText;
  };

  const getExpiryDateSubTextClass = () => {
    let expiryDateSubTextClass = '';
    const keyStatus = keyDetails.status;
    if (keyStatus === 'EXPIRED') {
        expiryDateSubTextClass = classes.expiredKeyStatus;
    }
    return expiryDateSubTextClass;
  };

  const { apiKey } = keyDetails;

    return (
      <>
        {showLoading && <Loading />}
        <Grid
          container
          className={classes.singleKeyViewContainer}
          data-apim-test={`${apiKey}-single-key-view`}
        >
          {renderSecretDialog()}
          <Grid item xs={6} className={classes.leftPanel}>
            <h3>{getI18nFormattedMessage('label.application.details.key.overview.oauth.title')}</h3>
            {items.map((item) => displayItem(keyDetails, classes, item))}
            <div className={classes.buttonContainer}>
                {getLeftPanelButton()}
                {locked &&
                  <Tooltip
                    title={getI18nFormattedMessage(disabledByInternal? 'label.application.details.locked.by.internal.tooltip':
                    'label.application.details.keys.locked.tooltip')}
                    arrow placement="right">
                    <Lock className={classes.lockIcon}/>
                  </Tooltip>
                }
                {!locked && isLocking &&
                  <Tooltip
                    title={getI18nFormattedMessage('label.application.details.keys.locking.tooltip')}
                    arrow placement="right">
                    <ReportProblem className={classes.warningIcon}/>
                  </Tooltip>
                }
              </div>
          </Grid>
          <Grid item xs={6} className={classes.rightPanel}>
            <h3>{getI18nFormattedMessage('label.application.details.key.overview.apikey.title')}</h3>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-key-${keyDetails.apiKey}`}
                name={intl.getI18nMessage('label.application.key')}
                value={get(keyDetails, 'apiKey')}
                disabled
                fieldContainerClass={classes.textField}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyDetails.apiKey);
                  showCopied(keyDetails.apiKey, 'API_KEY', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>
            </div>
            <div className={classes.fieldCopyContainer}>
              <FormTextField
                id={`client-secret-password-${keyDetails.keySecret}`}
                name={intl.getI18nMessage('label.application.key.shared.secret')}
                value={get(keyDetails, 'keySecret')}
                fieldContainerClass={classes.textField}
                disabled
              />
              {!keyDetails.keySecretHashed &&
              <Button
                variant="outlined"
                onClick={() => {
                  copy(keyDetails.keySecret);
                  showCopied(keyDetails.keySecret, 'SECRET', notifyMessages);
                }}
                className={classes.copyButton}
                >
                {getI18nFormattedMessage('label.application.details.keys.overview.copy.button')}
              </Button>}
            </div>
            <Fragment>
              <Grid container className={classes.gridContainer}>
                <Grid md={12} item>
                  <Fragment>
                    <Grid container>
                      <Grid xs={12} sm={10} item className={classes.generateSecretContainer}>
                        <Tooltip
                          title={getI18nFormattedMessage('label.application.key.shared.secret.generate.new.tool.tip')}
                          arrow placement="top"
                        >
                            <Button
                              color="primary"
                              variant="outlined"
                              className={classes.generateSecretMenuButton}
                              aria-controls={'basic-menu'}
                              aria-haspopup="true"
                              aria-expanded="true"
                              id={`btn-key-generate-secret-${idx}`}
                              onClick={handleGenerateSecretClick}
                              disabled={locked || isEditDisabled || get(keyDetails, 'status') === API_KEY_STATUS_DELETE_FAILED}
                            >
                              {getI18nFormattedMessage('label.application.details.keys.generatenew')}
                              {displayHashingOptions &&
                                <KeyboardArrowDown />
                              }
                            </Button>
                        </Tooltip>
                        {displayHashingOptions &&
                          <Menu
                            id="secret-type-menu"
                            anchorEl={anchorEl}
                            open={anchorEl}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            onClose={handleMenuClose}
                            MenuListProps={{
                              'aria-labelledby': 'basic-button',
                            }}
                            disabled={locked || isEditDisabled || get(keyDetails, 'status') === API_KEY_STATUS_DELETE_FAILED}
                          >
                            <MenuItem onClick={() => handleGenerateSecretByType('PLAIN_SECRET')}>
                              {getI18nFormattedMessage('label.application.details.keys.generatenew.plain')}
                            </MenuItem>
                            <MenuItem onClick={() => handleGenerateSecretByType('HASHED_SECRET')}>
                              {getI18nFormattedMessage('label.application.details.keys.generatenew.hashed')}
                            </MenuItem>
                          </Menu>
                        }
                      {locked &&
                        <Tooltip
                          title={getI18nFormattedMessage(disabledByInternal? 'label.application.details.locked.by.internal.tooltip':
                          'label.application.details.keys.locked.tooltip')}
                          arrow placement="right">
                          <Lock className={classes.lockIcon2}/>
                        </Tooltip>
                      }
                      {!locked && isLocking &&
                        <Tooltip
                          title={getI18nFormattedMessage('label.application.details.keys.locking.tooltip')}
                          arrow placement="right">
                          <ReportProblem className={classes.warningIcon2}/>
                        </Tooltip>
                      }
                      </Grid>
                    </Grid>
                  </Fragment>
                </Grid>
              </Grid>
            </Fragment>
            {isApiKeyExpiryEnabled && <div>
              <h3 data-apim-test="expiry-title">
                {getI18nFormattedMessage('label.application.details.key.expiry.date.title')}
              </h3>
              <div data-apim-test="expiry-label">
                <Typography variant="body1" className={classes.expiryDateText}>
                  {getExpiryDateText()}
                </Typography>
              </div>
              <div data-apim-test="expiry-sub-label">
                <Typography variant="body1" className={getExpiryDateSubTextClass()}>
                  {getExpiryDateSubText()}
                </Typography>
              </div>
            </div>}
          </Grid>
        </Grid>
      </>
    );
};

SingleKeyView.propTypes = {
  keyDetails: object,
  classes: object,
  notifyMessages: func,
  isAdmin: bool,
  isApiKeyExpiryEnabled: bool,
  setIsEditKeyOpen: func,
  isLocking: bool,
  locked: bool,
  disabledByInternal: bool,
  appSecretHashingMetadata: object,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  deleteKey: func,
  regenerateSecret: func,
  onSaveDone: func,
  applicationUuid: string,
  idx: number,
  defaultHashType: string,
  isEditDisabled: bool,
  isPortalAdmin: bool,
};

export default SingleKeyView;